import { cn } from "@babybjorn/ui";
import type { ISbRichtext } from "gatsby-source-storyblok";
import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type CSSProperties,
	type FC,
} from "react";
import ScrollableTabs from "../../../ScrollableTabs";
import Tab from "../Tab";
import * as s from "./TabGroup.module.scss";

export type TabGroupTabsProps = {
	id?: string | number;
	label: string;
	header?: string;
	richtext?: string | ISbRichtext;
	cta?: { label: string; link: string };
	image?: { filename: string | null; alt: string | null };
}[];

type TabGroupProps = {
	selectedTabIndex: number;
	setSelectedTabIndex: (id: number) => void;
	tabLabels: string[];
	ariaLabelScroll: string;
};

const TabGroup: FC<TabGroupProps> = ({
	tabLabels,
	setSelectedTabIndex,
	selectedTabIndex,
	ariaLabelScroll,
}) => {
	const timeoutRef = useRef<NodeJS.Timeout>();

	const wrapperRef = useRef<HTMLDivElement>(null);
	const tabRefs = useRef<unknown[]>([]);

	const [sliderValues, setSliderValues] = useState<CSSProperties>({});
	const [, setShouldScroll] = useState(false);

	const [isScrolling, setIsScrolling] = useState(false);

	const calcSliderValues = useCallback(() => {
		const wrapper = wrapperRef.current;
		const activeTab = tabRefs.current[selectedTabIndex] as
			| HTMLLIElement
			| undefined;

		if (wrapper && activeTab) {
			const wrapperRect = wrapper.getBoundingClientRect();
			const activeRect = activeTab.getBoundingClientRect();

			setSliderValues({
				transform: `translateX(${activeRect.left - wrapperRect.left}px)`,
				width: `${activeTab.offsetWidth}px`,
			});
		}
	}, [selectedTabIndex]);

	useEffect(() => {
		calcSliderValues();

		window.addEventListener("resize", calcSliderValues);
		return () => {
			window.removeEventListener("resize", calcSliderValues);
		};
	}, [calcSliderValues]);

	const scrollHandler = () => {
		clearTimeout(timeoutRef.current);

		setIsScrolling(true);

		timeoutRef.current = setTimeout(() => {
			setIsScrolling(false);
		}, 100);

		calcSliderValues();
	};

	const shouldScrollHandler = (newShouldScroll: boolean) => {
		setShouldScroll(newShouldScroll);
	};

	return (
		<div ref={wrapperRef} className={cn(s.wrapper)}>
			<ScrollableTabs
				className={s.groupTabs}
				onShouldScroll={shouldScrollHandler}
				onScroll={scrollHandler}
				ariaLabelScroll={ariaLabelScroll}
			>
				{tabLabels.map((label, index) => (
					<Tab
						ref={(el) => (tabRefs.current[index] = el)}
						key={`${label}-${index}`}
						index={index}
						label={label}
						onClick={() => setSelectedTabIndex(index)}
						active={selectedTabIndex === index}
					/>
				))}
			</ScrollableTabs>

			{wrapperRef.current?.scrollWidth && (
				<div className={s.slider}>
					<span
						className={cn(s.sliderActive, {
							[s.isScrolling]: isScrolling,
						})}
						style={sliderValues}
					/>
				</div>
			)}
		</div>
	);
};
export default TabGroup;
